/**
 * AutoTemplate Format Types
 *
 * Defines the canonical template `manifest.json` file format — a self-contained
 * bundle that includes a workflow definition, mock data, trigger sample data,
 * and connections in a single file.
 *
 * SOURCE OF TRUTH: the JSON Schema published by the templates repo at
 * `Azure/ProjectAutoTemplates/schemas/template.schema.json` (also deployed to
 * storage at `/schemas/template.schema.json`). This file is a hand-mirrored Zod
 * equivalent — Zod gives us the compile-time `AutoTemplate` type and
 * synchronous, offline-safe validation that a runtime-fetched JSON schema
 * cannot. When the published schema changes, update this file in the same
 * release. Categories deliberately retain tolerant import/storage validation;
 * generation/export applies the stricter authoring rules in `template.ts`.
 *
 * This is the *file-format* type. Runtime state shapes live in
 * `templateMode.ts`. An adapter layer in `data/templates/templateAdapter.ts`
 * converts AutoTemplate → TemplateDefinition + TemplateExtras.
 */

import { z } from 'zod';
import { DefinitionSchema } from '@/types/workflowDefinition';

// =============================================================================
// SCHEMAS
// =============================================================================

const autoTemplateMetadataSchema = z.object({
  /** Stable, URL-safe identifier (e.g. "tool-call-uri-http"). */
  id: z.string().min(1),
  /** Human-readable template name. */
  name: z.string().min(1),
  /** One-to-two sentence description. */
  description: z.string().min(1),
  /**
   * Non-empty free-form categories for backward-compatible import/storage.
   * The adapter normalizes recognized IDs and falls back to Other if none
   * remain. Generation/export validates strict authoring rules separately.
   */
  category: z.array(z.string().min(1)).min(1),
  /** Search / filter tags. */
  tags: z.array(z.string().min(1)).default([]),
  /** Author or organisation. */
  author: z.string().min(1),
  /** Provenance label (e.g. "builtin", "community"). */
  source: z.string().min(1),
  /**
   * Connector ids rendered as icons on gallery cards, e.g.
   * '/managedApis/office365', '/serviceProviders/AzureBlob',
   * 'connectionProviders/request'. Display names and icons are resolved at
   * runtime from the id — nothing else is stored.
   */
  featuredConnectors: z.array(z.string().min(1)).min(1),
});

const autoTemplateTriggerSchema = z.object({
  /** Trigger action name (must match a key in workflow.definition.triggers). */
  name: z.string().min(1),
  /** Sample outputs used to pre-fill the Test Draft editor. */
  outputs: z.object({
    method: z.string().min(1),
    headers: z.record(z.string(), z.unknown()),
    queries: z.record(z.string(), z.unknown()),
    body: z.unknown().optional(),
  }),
});

// Per-action mock entry. Presence of an entry means "replace this action's
// live call with the canned output"; actions that should run live are simply
// omitted from `mocks`.
const autoTemplateMockOutputSchema = z.object({
  /** Mock run status. */
  status: z.enum(['Succeeded', 'Failed']),
  outputs: z.object({
    statusCode: z.string().min(1),
    body: z.unknown().optional(),
    headers: z.record(z.string(), z.unknown()).optional(),
  }),
});

// The schema still admits `{ "runForReal": true }` entries as an explicit
// opt-out. They parse to null and are dropped in the `mocks` transform — an
// action without mock data just runs live.
const runForRealSchema = z
  .object({ runForReal: z.literal(true) })
  .transform(() => null);

const autoTemplateMockEntrySchema = z.union([autoTemplateMockOutputSchema, runForRealSchema]);

const autoTemplateConnectionSchema = z.object({
  /** Connection type: 'shared' for managed APIs, 'inapp' for built-in connectors, 'agent' for agent connections */
  connectorType: z.enum(['shared', 'inapp', 'agent']),
  /** API identifier path, e.g. '/managedApis/sql' or '/serviceProviders/AzureBlob' */
  apiId: z.string().min(1),
});

export const autoTemplateSchema = z.object({
  $schema: z.string().optional(),
  kind: z.literal('AutoTemplate'),
  apiVersion: z.literal('v1'),

  metadata: autoTemplateMetadataSchema,
  /**
   * The bare Logic Apps workflow definition. Unlike the legacy format
   * there is no `{ definition, kind, notes }` wrapper — `.strict()` rejects
   * wrapper-shaped payloads instead of silently stripping them.
   */
  workflow: z.object({ definition: DefinitionSchema }).strict(),
  trigger: autoTemplateTriggerSchema,
  mocks: z
    .record(z.string(), autoTemplateMockEntrySchema)
    .default({})
    .transform((mocks) =>
      Object.fromEntries(
        Object.entries(mocks).filter(
          (entry): entry is [string, z.infer<typeof autoTemplateMockOutputSchema>] =>
            entry[1] !== null,
        ),
      ),
    ),
  connections: z.record(
    z.string().regex(/^\S*_#workflowname#$/, {
      message: 'connections key must end with _#workflowname#',
    }),
    autoTemplateConnectionSchema,
  ).default({}),
});

// =============================================================================
// TYPES
// =============================================================================

export type AutoTemplate = z.infer<typeof autoTemplateSchema>;
export type AutoTemplateMetadata = z.infer<typeof autoTemplateMetadataSchema>;
export type AutoTemplateTrigger = z.infer<typeof autoTemplateTriggerSchema>;
export type AutoTemplateMockOutput = z.infer<typeof autoTemplateMockOutputSchema>;
export type AutoTemplateConnection = z.infer<typeof autoTemplateConnectionSchema>;

// =============================================================================
// VALIDATION HELPER
// =============================================================================

/**
 * Validate an unknown value against `autoTemplateSchema`.
 *
 * Returns a discriminated result:
 * - `{ success: true,  data: AutoTemplate }` on success
 * - `{ success: false, errors: string[] }` on failure
 */
export function validateAutoTemplate(
  data: unknown,
): { success: true; data: AutoTemplate } | { success: false; errors: string[] } {
  const result = autoTemplateSchema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const errors = result.error.issues.map((issue) => {
    const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
    return `${path}${issue.message}`;
  });

  return { success: false, errors };
}
